import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import StatusPill from '../components/StatusPill';
import ServiceForm from '../components/ServiceForm';
import ServiceHistory from '../components/ServiceHistory';
import Modal from '../components/Modal';
import { useApi } from '../hooks/useApi';
const ServicesPage = () => {
    const { get, post, put, del } = useApi();
    const [services, setServices] = useState([]);
    const [checks, setChecks] = useState([]);
    const [checksPage, setChecksPage] = useState(0);
    const [hasMoreChecks, setHasMoreChecks] = useState(false);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalService, setModalService] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerService, setDrawerService] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const loadServices = async () => {
        setLoading(true);
        try {
            const data = await get('/services');
            setServices(data);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchChecks = async (serviceId, page = 0) => {
        const limit = 20;
        const offset = page * limit;
        const data = await get(`/services/${serviceId}/checks?limit=${limit}&offset=${offset}`);
        setChecks(data.items);
        setHasMoreChecks(offset + data.items.length < data.total);
    };
    useEffect(() => {
        loadServices();
    }, []);
    const handleSave = async (payload, isNew) => {
        if (isNew) {
            const created = await post('/services', payload);
            setServices((prev) => [...prev, created]);
        }
        else if (modalService) {
            const updated = await put(`/services/${modalService.id}`, payload);
            setServices((prev) => prev.map((service) => (service.id === updated.id ? updated : service)));
            setModalService(updated);
        }
    };
    const handleDelete = async (id) => {
        await del(`/services/${id}`);
        setServices((prev) => prev.filter((service) => service.id !== id));
        if (drawerService?.id === id) {
            setDrawerOpen(false);
            setDrawerService(null);
        }
        setChecks([]);
    };
    const openCreateModal = () => {
        setModalService(null);
        setModalOpen(true);
    };
    const openEditModal = (service) => {
        setModalService(service);
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
    };
    const openDrawer = async (service) => {
        setDrawerService(service);
        setDrawerOpen(true);
        await fetchChecks(service.id, 0);
        setChecksPage(0);
    };
    const closeDrawer = () => {
        setDrawerOpen(false);
        setDrawerService(null);
        setChecks([]);
        setChecksPage(0);
        setHasMoreChecks(false);
    };
    return (_jsxs(AppLayout, { title: "Services", actions: _jsx("button", { onClick: openCreateModal, disabled: loading, children: "New service" }), children: [_jsxs("div", { className: "card", children: [_jsx("h3", { style: { marginTop: 0 }, children: "Configured services" }), _jsxs("table", { className: "table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "Interval" }), _jsx("th", { children: "Timeout" }), _jsx("th", { children: "Target" }), _jsx("th", { style: { textAlign: 'right' }, children: "Actions" })] }) }), _jsxs("tbody", { children: [services.map((service) => (_jsxs("tr", { children: [_jsx("td", { children: service.name }), _jsx("td", { children: _jsx(StatusPill, { online: service.lastStatus ?? undefined }) }), _jsxs("td", { children: [service.pollingIntervalSeconds, "s"] }), _jsxs("td", { children: [service.responseTimeoutMs, "ms"] }), _jsx("td", { style: { maxWidth: '320px', wordBreak: 'break-word' }, children: service.targetUrl ||
                                                    `${service.serverAddress}${service.endpointPath}` }), _jsxs("td", { style: { textAlign: 'right' }, children: [_jsx("button", { type: "button", className: "icon-button", title: "Recent checks", onClick: () => openDrawer(service), children: "\u2630" }), _jsx("button", { type: "button", className: "icon-button", title: "Edit", onClick: () => openEditModal(service), children: "\u270F\uFE0F" }), _jsx("button", { type: "button", className: "icon-button icon-button--danger", title: "Delete", onClick: () => setDeleteTarget(service), children: "\uD83D\uDDD1\uFE0F" })] })] }, service.id))), !services.length && !loading && (_jsx("tr", { children: _jsx("td", { colSpan: 6, style: { textAlign: 'center', padding: '1rem' }, children: "No services yet. Create one to start monitoring." }) })), loading && (_jsx("tr", { children: _jsx("td", { colSpan: 6, style: { textAlign: 'center', padding: '1rem' }, children: "Loading services\u2026" }) }))] })] })] }), _jsx(Modal, { open: modalOpen, title: modalService ? `Edit ${modalService.name}` : 'Create service', onClose: closeModal, width: "min(600px, 95vw)", children: _jsx(ServiceForm, { service: modalService, onSave: async (payload, isNew) => {
                        await handleSave(payload, isNew);
                        closeModal();
                    }, onDelete: modalService
                        ? async (id) => {
                            await handleDelete(id);
                            closeModal();
                        }
                        : undefined }) }), _jsxs(Modal, { open: Boolean(deleteTarget), title: "Confirm removal", onClose: () => setDeleteTarget(null), width: "min(420px, 90vw)", children: [_jsxs("p", { children: ["Remove service", ' ', _jsx("strong", { children: deleteTarget ? deleteTarget.name : '' }), "? This action cannot be undone."] }), _jsxs("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }, children: [_jsx("button", { type: "button", style: {
                                    background: 'transparent',
                                    border: '1px solid var(--color-border)',
                                    color: 'var(--color-text)'
                                }, onClick: () => setDeleteTarget(null), children: "Cancel" }), _jsx("button", { type: "button", style: { background: 'tomato', color: '#000' }, onClick: async () => {
                                    if (deleteTarget) {
                                        await handleDelete(deleteTarget.id);
                                    }
                                    setDeleteTarget(null);
                                }, children: "Delete" })] })] }), drawerOpen && drawerService && (_jsx("div", { className: "drawer", children: _jsxs("div", { className: "drawer__panel", children: [_jsxs("div", { className: "drawer__header", children: [_jsxs("div", { children: [_jsx("h3", { style: { margin: 0 }, children: drawerService.name }), _jsx("p", { style: { margin: 0, color: 'rgba(255,255,255,0.7)' }, children: drawerService.targetUrl ||
                                                `${drawerService.serverAddress}${drawerService.endpointPath}` })] }), _jsx("button", { type: "button", className: "icon-button", onClick: closeDrawer, children: "\u00D7" })] }), _jsx(ServiceHistory, { checks: checks, onRefresh: () => drawerService && fetchChecks(drawerService.id, checksPage), page: checksPage, hasMore: hasMoreChecks, onPageChange: (page) => {
                                if (!drawerService)
                                    return;
                                setChecksPage(page);
                                fetchChecks(drawerService.id, page);
                            } })] }) }))] }));
};
export default ServicesPage;
